import { useEffect, useState } from 'react';

export type Platform = 'ios' | 'android' | 'unknown';

/**
 * Best-effort client-side platform hint from `navigator`. Returns `'unknown'`
 * on the server and during the first client render (so the initial markup is
 * stable and hydration-safe), then resolves to `ios` / `android` /
 * `unknown` after mount.
 *
 * Used only to *reorder* download options — every platform is always shown,
 * so a wrong guess degrades to the default order, never to a missing link.
 */
export function detectPlatform(navigatorLike: Navigator = navigator): Platform {
  if (typeof navigatorLike === 'undefined') return 'unknown';
  const ua = navigatorLike.userAgent || '';
  // iPadOS 13+ reports as Mac Safari with touch support; check for it explicitly.
  const isMacTouch =
    /Macintosh/.test(ua) &&
    typeof document !== 'undefined' &&
    'ontouchend' in document;
  if (/iPhone|iPad|iPod/i.test(ua) || isMacTouch) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'unknown';
}

export function usePlatform(): Platform {
  // Render `'unknown'` first to keep SSR/first-paint markup deterministic,
  // then re-render with the real hint once mounted.
  const [platform, setPlatform] = useState<Platform>('unknown');
  useEffect(() => {
    setPlatform(detectPlatform());
  }, []);
  return platform;
}
